/**
 * Weighted draw without replacement: P(x) proportional to popularity(x)^(-lambda).
 *
 * At lambda = 1.6, number 7 on the Loto goes from a 10% to a 6.9% chance of being
 * selected. It still comes out — that is what a random draw does. Never re-roll a
 * generation to get "better" numbers: sorting after the fact puts back exactly the
 * human bias the procedure removes.
 */
import type { GameConfig } from './games';
import { structuralOk } from './constraints';
import { popularityProfile, DEFAULT_COEF } from './popularity';
import type { Coefficients } from './popularity';
import { randomUnit } from './random';

export interface Grid {
  readonly main: readonly number[];
  readonly bonus: readonly number[];
}

export const MAX_TRIES = 200_000;

/** Pick k distinct indices in 1..weights.length, proportionally to `weights`. */
export function pickWeighted(weights: readonly number[], k: number): number[] {
  const w = [...weights];
  const out: number[] = [];
  for (let pick = 0; pick < k; pick++) {
    let total = 0;
    for (const x of w) total += x;
    if (!(total > 0)) throw new Error('poids epuises : k superieur au nombre de numeros');
    let r = randomUnit() * total;
    let j = 0;
    while (j < w.length - 1 && r > (w[j] as number)) {
      r -= w[j] as number;
      j++;
    }
    out.push(j + 1);
    w[j] = 0;
  }
  return out.sort((a, b) => a - b);
}

const weightCache = new Map<string, readonly number[]>();

function inverseWeights(
  nmax: number,
  lambda: number,
  coef: Coefficients,
): readonly number[] {
  const key = [
    nmax,
    lambda,
    coef.date_low,
    coef.date_high,
    coef.month,
    coef.odd,
    coef.prime,
    coef.round,
    coef.seven,
    coef.thirteen,
    coef.high_tail,
  ].join('|');
  const hit = weightCache.get(key);
  if (hit !== undefined) return hit;
  const w = Object.freeze(popularityProfile(nmax, coef).map((p) => Math.pow(p, -lambda)));
  weightCache.set(key, w);
  return w;
}

export interface DrawOptions {
  /** Defaults to the game's derived lambda. Forcing it is for experiments only. */
  lambda?: number;
  coef?: Coefficients;
  tries?: number;
}

export function drawGrid(g: GameConfig, options: DrawOptions = {}): Grid {
  const lambda = options.lambda ?? g.lambda;
  const coef = options.coef ?? DEFAULT_COEF;
  const tries = options.tries ?? MAX_TRIES;

  const wm = inverseWeights(g.n, lambda, coef);
  const wb = inverseWeights(g.bmax, lambda, coef);

  for (let i = 0; i < tries; i++) {
    const main = pickWeighted(wm, g.k);
    if (structuralOk(main, g)) {
      return { main, bonus: pickWeighted(wb, g.bk) };
    }
  }
  throw new Error('contraintes trop serrees : baissez lambda');
}
